import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(undefined);

const THEME_KEY = 'theme-preference';

export function ThemeProvider({ children }) {
  // Initialize from localStorage or default to 'light'
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(THEME_KEY) || 'light';
    }
    return 'light';
  });

  // Track the effective theme (what's actually displayed)
  const [effectiveTheme, setEffectiveTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_KEY);
      if (stored === 'dark') return 'dark';
      return 'light';
    }
    return 'light';
  });

  // Apply theme to document
  const applyTheme = useCallback((themeToApply) => {
    document.documentElement.setAttribute('data-theme', themeToApply);
    setEffectiveTheme(themeToApply);
  }, []);

  // Handle theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Persist theme preference
  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // Set specific theme
  const setTheme = useCallback((newTheme) => {
    if (['light', 'dark'].includes(newTheme)) {
      setThemeState(newTheme);
    }
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    setThemeState((current) => current === 'light' ? 'dark' : 'light');
  }, []);

  // Check if dark mode is active
  const isDark = effectiveTheme === 'dark';

  const value = {
    theme,           // User's preference: 'light' | 'dark' | 'system'
    effectiveTheme,  // What's actually displayed: 'light' | 'dark'
    isDark,          // Convenience boolean
    setTheme,        // Set specific theme
    toggleTheme,     // Cycle through themes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
